"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NotificationService = void 0;
const firebaseManager_1 = require("./firebaseManager");
const firestore_1 = require("firebase/firestore");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
const core_1 = require("@capacitor/core");
class NotificationService {
    /**
     * Subscribe to user notifications
     * NOTE: Real-time subscriptions (onSnapshot) only work on web platform
     * Native platforms should use polling with getUserNotifications()
     */
    static subscribeToNotifications(userId, callback, limitCount = 50) {
        if (!(0, firebaseManager_1.isFirebaseInitialized)()) {
            console.error('Firebase not initialized for notifications subscription');
            return () => { }; // Return empty unsubscribe function
        }
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            // Native platforms don't support onSnapshot with Capacitor Firestore
            // Use polling instead
            console.log('📱 NotificationService: Native platform detected - using polling instead of onSnapshot');
            let isActive = true;
            const pollInterval = 30000; // Poll every 30 seconds
            // Initial fetch
            this.getUserNotifications(userId, limitCount).then(callback);
            // Set up polling
            const intervalId = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                if (isActive) {
                    const notifications = yield this.getUserNotifications(userId, limitCount);
                    callback(notifications);
                }
            }), pollInterval);
            // Return unsubscribe function
            return () => {
                isActive = false;
                clearInterval(intervalId);
                console.log('📱 NotificationService: Stopped polling notifications');
            };
        }
        else {
            // Web platform - use real-time onSnapshot
            console.log('🌐 NotificationService: Web platform detected - using onSnapshot');
            const notificationsQuery = (0, firestore_1.query)((0, firestore_1.collection)((0, firebaseManager_1.getDb)(), 'notifications'), (0, firestore_1.where)('userId', '==', userId), (0, firestore_1.orderBy)('timestamp', 'desc'), (0, firestore_1.limit)(limitCount));
            return (0, firestore_1.onSnapshot)(notificationsQuery, (snapshot) => {
                const notifications = snapshot.docs.map(doc => (Object.assign({ id: doc.id }, doc.data())));
                callback(notifications);
            });
        }
    }
    /**
     * Get user notifications (one-time fetch)
     */
    static getUserNotifications(userId, limitCount = 50) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, firebaseManager_1.isFirebaseInitialized)()) {
                console.error('Firebase not initialized for getUserNotifications');
                return [];
            }
            console.log('🔍 NotificationService: Querying notifications for userId:', userId);
            console.log('📊 NotificationService: Executing Firestore query...');
            const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                .where('userId', '==', userId)
                .orderBy('timestamp', 'desc')
                .limit(limitCount)
                .getDocs();
            console.log('✅ NotificationService: Got', snapshot.length, 'notifications');
            // Log first and last notification timestamps for debugging iOS cache issues
            if (snapshot.length > 0) {
                const firstDoc = snapshot[0].data();
                const lastDoc = snapshot[snapshot.length - 1].data();
                console.log('📅 NotificationService: Most recent timestamp:', firstDoc.timestamp || firstDoc.createdAtISO);
                console.log('📅 NotificationService: Oldest timestamp:', lastDoc.timestamp || lastDoc.createdAtISO);
                console.log('🔔 NotificationService: First notification type:', firstDoc.type, 'message:', (_a = firstDoc.message) === null || _a === void 0 ? void 0 : _a.substring(0, 50));
            }
            return snapshot.map(doc => (Object.assign({ id: doc.id }, doc.data())));
        });
    }
    /**
     * Get unread notification count
     */
    static getUnreadCount(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, firebaseManager_1.isFirebaseInitialized)()) {
                console.error('Firebase not initialized for getUnreadCount');
                return 0;
            }
            console.log('🔍 NotificationService: Getting unread count for userId:', userId);
            const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                .where('userId', '==', userId)
                .where('read', '==', false)
                .getDocs();
            console.log('✅ NotificationService: Unread count:', snapshot.length);
            return snapshot.length;
        });
    }
    /**
     * Mark notification as read
     */
    static markAsRead(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('📝 NotificationService: Marking notification as read:', notificationId);
            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('notifications', notificationId, {
                read: true
            });
        });
    }
    /**
     * Mark all notifications as read for a user
     */
    static markAllAsRead(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('📝 NotificationService: Marking all notifications as read for userId:', userId);
            const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                .where('userId', '==', userId)
                .where('read', '==', false)
                .getDocs();
            const updatePromises = snapshot.map(doc => (0, unifiedFirestoreService_1.unifiedUpdateDoc)('notifications', doc.id, { read: true }));
            yield Promise.all(updatePromises);
            console.log('✅ NotificationService: Marked', snapshot.length, 'notifications as read');
        });
    }
    /**
     * Delete a notification
     */
    static deleteNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🗑️ NotificationService: Deleting notification:', notificationId);
            yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)('notifications', notificationId);
        });
    }
    /**
     * Delete old notifications for a user (older than 30 days)
     */
    static deleteOldNotifications(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🗑️ NotificationService: Deleting old notifications for userId:', userId);
            const thirtyDaysAgo = firestore_1.Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
            const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                .where('userId', '==', userId)
                .where('timestamp', '<', thirtyDaysAgo)
                .getDocs();
            const deletePromises = snapshot.map(doc => (0, unifiedFirestoreService_1.unifiedDeleteDoc)('notifications', doc.id));
            yield Promise.all(deletePromises);
            console.log('✅ NotificationService: Deleted', snapshot.length, 'old notifications');
        });
    }
    /**
     * Format notification for display
     */
    static formatNotificationMessage(notification) {
        switch (notification.type) {
            case 'movie_added':
                return notification.message;
            case 'collaborator_added':
                return notification.message;
            case 'movie_watched':
                return notification.message;
            case 'list_shared':
                return notification.message;
            default:
                return notification.message;
        }
    }
    /**
     * Get notification icon based on type
     */
    static getNotificationIcon(type) {
        switch (type) {
            case 'movie_added':
                return '🎬';
            case 'collaborator_added':
                return '👥';
            case 'movie_watched':
                return '✅';
            case 'list_shared':
                return '📋';
            case 'user_followed':
                return '👤';
            case 'watchlist_liked':
                return '❤️';
            case 'review_liked':
                return '❤️';
            default:
                return '🔔';
        }
    }
    /**
     * Get notification action URL
     */
    static getNotificationActionUrl(notification) {
        switch (notification.type) {
            case 'movie_added':
            case 'movie_watched':
                if (notification.relatedWatchlistId) {
                    return `/watchlist/${notification.relatedWatchlistId}`;
                }
                if (notification.relatedMovieId) {
                    return `/movie/${notification.relatedMovieId}`;
                }
                break;
            case 'collaborator_added':
            case 'list_shared':
            case 'watchlist_liked':
                if (notification.relatedWatchlistId) {
                    return `/watchlist/${notification.relatedWatchlistId}`;
                }
                break;
            case 'user_followed':
                if (notification.relatedUserId) {
                    return `/user/${notification.relatedUserId}`;
                }
                break;
            case 'review_liked':
                if (notification.relatedMovieId) {
                    return `/movie/${notification.relatedMovieId}`;
                }
                break;
        }
        return '/notifications';
    }
    /**
     * Create a test notification (for development)
     */
    static createTestNotification(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            // This would typically be done by Cloud Functions
            // This is just for testing purposes
            console.log('Test notification would be created for user:', userId);
        });
    }
}
exports.NotificationService = NotificationService;
